import { router } from '@inertiajs/core';
import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode, useEffect, useState } from 'react';
import { Folder, Mail as MailIcon } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, CartesianGridProps, Line, LineChart, LineProps, ResponsiveContainer, ResponsiveContainerProps, Tooltip, TooltipProps, XAxis, XAxisProps, YAxis, YAxisProps } from 'recharts';
import { CategoricalChartProps } from 'recharts/types/chart/generateCategoricalChart';

import { assetUrl } from '../../Shared/helpers';
import { useLocale } from '../../Shared/locale';
import { Campaign, Mail, MailType, Project, SentMail, SentMailLink } from '../../Shared/types';
import { Card } from '../../Shared/UI/Card';
import { Icon } from '../../Shared/UI/Icon';
import Picker, { PickerItem } from '../../Shared/UI/Picker';
import { CampaignLogo } from '../Campaigns/CampaignLogo';
import { useRouter } from '../router';
import { Charity, SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';
import { DashboardLayout } from './DashboardLayout';

const defaultChartProps: {
  container: Partial<ResponsiveContainerProps>;
  chart: Partial<CategoricalChartProps>;
  grid: Partial<CartesianGridProps>;
  xAxis: Partial<Omit<XAxisProps, 'ref'>>;
  yAxis: Partial<Omit<YAxisProps, 'ref'>>;
  line: Partial<Omit<LineProps, 'ref'>>;
} = {
  container: {
    width: '100%',
    height: 250,
  },
  chart: {
    height: 250,
    margin: {
      top: 15,
      right: 15,
      left: 0,
      bottom: 15,
    },
  },
  grid: {
    stroke: '#e5e7eb',
    strokeDasharray: '5 5',
    vertical: false,
  },
  xAxis: {
    type: 'category',
    tick: { fontSize: 10 },
    stroke: '#616774',
    dy: 10,
  },
  yAxis: {
    tick: { fontSize: 10 },
    strokeWidth: 0,
  },
  line: {
    type: 'monotone',
    dataKey: 'value',
    strokeWidth: 2,
    dot: false,
  },
};

interface MailDashboardPageProps extends SharedProps {
  sentMailCounts: {
    date: string;
    value: number;
  }[];
  totals: {
    overall: {
      sent_count: number;
      open_count: number;
      open_rate: number | null;
      click_count: number;
      click_rate: number | null;
    };
    today: {
      sent_count: number;
      open_count: number;
      click_count: number;
    };
  };
  recentSentMails: (SentMail & {
    campaign: Campaign & {
      project: Project;
    };
    mail: Mail;
  })[];
  topLinks: (SentMailLink & {
    click_count: number;
  })[];
  topCustomMails: (Mail & {
    project: Project;
    sent_count: number;
  })[];
  projects: (Project & {
    mail_types: MailType[];
  })[];
  mails: PickerItem[];
  mailTypes: MailType[];
}

export default function MailDashboardPage({
  sentMailCounts,
  totals,
  recentSentMails,
  topLinks,
  topCustomMails,
  projects,
  mails,
  mailTypes,
  charity,
  params,
}: MailDashboardPageProps) {
  const { t } = useTranslation();
  const { formatNumber, formatPercentage, formatDate } = useLocale();

  const [showOpenCount, setShowOpenCount] = useState(false);
  const [showClickCount, setShowClickCount] = useState(false);

  // Refresh dashboard every 15 seconds.
  useEffect(() => {
    const timer = window.setInterval(() => {
      router.reload();
    }, 15_000);

    return () => window.clearInterval(timer);
  });

  const activityListMaxLength = Math.max(recentSentMails.length, topCustomMails.length, topLinks.length);

  const defaultMails: PickerItem[] = mailTypes.filter((type) => [
    MailType.NewCampaign,
    MailType.DonationReceived,
    MailType.Reminder,
    MailType.TargetReached,
    MailType.UpcomingEvent,
    MailType.UpcomingEventReminder,
    MailType.NewTeamMember,
  ].includes(type)).map((type) => ({
    id: type,
    title: t(`backend:${type}_mail`),
  }));

  const filteredProjectId = typeof params.project === 'string' ? params.project : null;
  const filteredMailId = typeof params.mail === 'string' ? params.mail : null;

  return (
    <>
      <Header>
        <Container>
          <div className="items-center justify-between sm:flex">
            <HeaderTitle>
              {t('backend:dashboard')}
            </HeaderTitle>
            <div className="sm:flex space-y-2 sm:space-y-0 sm:space-x-2">
              <div className="w-full sm:w-[300px]">
                <Picker
                  name="project"
                  items={projects}
                  selected={filteredProjectId}
                  icon={<Folder />}
                  optional
                  placeholder={(t('backend:all_projects'))}
                  payload={(projectId) => {
                    if (filteredMailId) {
                      const allowedMailTypes = projectId
                        ? projects.filter((project) => project.id === projectId)[0]?.mail_types || []
                        : mailTypes;

                      if (allowedMailTypes.includes(filteredMailId as MailType)) {
                        return { mail: filteredMailId };
                      }
                    }

                    return { mail: undefined };
                  }}
                />
              </div>
              <div className="w-full sm:w-[300px]">
                <Picker
                  name="mail"
                  items={[...mails, ...defaultMails]}
                  selected={filteredMailId}
                  icon={<MailIcon />}
                  optional
                  placeholder={(t('backend:all_mails'))}
                />
              </div>
            </div>
          </div>
        </Container>
      </Header>
      <Container className="space-y-6">
        <div className="grid gap-6 sm:grid-cols-1">
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:sent_emails')}
              </h4>
            </div>
            <div className="flex items-center justify-center flex-1">
              {sentMailCounts.length === 0 && (
                <div className="flex items-center justify-center flex-1">
                  <div className="text-center text-slate-400">
                    <img src={assetUrl('images/dashboard-empty.svg')} width={150} className="block mx-auto mb-4" />
                    {t('backend:no_data_yet')}
                  </div>
                </div>
              )}
              {sentMailCounts.length > 0 && (
                <ResponsiveContainer {...defaultChartProps.container}>
                  <LineChart
                    {...defaultChartProps.chart}
                    data={sentMailCounts}
                  >
                    <CartesianGrid {...defaultChartProps.grid} />
                    <XAxis
                      {...defaultChartProps.xAxis}
                      dataKey="date"
                      tickFormatter={(value) => formatDate(value, value.length === 7 ? 'display_month' : 'display_date')}
                    />
                    <YAxis
                      {...defaultChartProps.yAxis}
                      tickFormatter={(value) => formatNumber(value)}
                      width={formatNumber(sentMailCounts[sentMailCounts.length - 1]?.value || 0).length * 6.2 + 18}
                    />
                    <Tooltip
                      content={({ active, payload, label }: TooltipProps<number, number>) => {
                        if (active && payload && payload.length) {
                          return (
                            <Card border className="!p-2 text-sm">
                              <strong className="block text-emerald-500">
                                {formatDate(label, label.length === 7 ? 'display_month' : 'display_date')}
                              </strong>
                              {t('backend:n_emails_sent', {
                                value: formatNumber(payload[0].value || 0), count: payload[0].value || 0,
                              })}
                            </Card>
                          );
                        }
                      }}
                    />
                    <Line
                      {...defaultChartProps.line}
                      stroke="#7575c2"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        <Card className="grid text-center divide-y sm:divide-y-0 sm:divide-x sm:grid-cols-3 bg-gradient-to-t from-slate-50" spacing="none">
          <div className="p-6 space-y-2">
            <h4 className="font-medium text-slate-500">
              {t('backend:sent_emails')}
            </h4>
            <div className="text-2xl">
              {formatNumber(totals.overall.sent_count)}
            </div>
            <span
              className={classNames(
                'inline-block px-1 py-0.5 text-sm font-medium',
                totals.today.sent_count > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
              )}
            >
              {t('backend:plus_value_today', { value: formatNumber(totals.today.sent_count) })}
            </span>
          </div>
          <div
            className="p-6 space-y-2"
            onMouseEnter={() => setShowOpenCount(true)}
            onMouseLeave={() => setShowOpenCount(false)}
          >
            <h4 className="font-medium text-slate-500">
              {t('backend:opened')}
            </h4>
            <div className="text-2xl">
              {!showOpenCount && (
                <>
                  {totals.overall.open_rate !== null && formatPercentage(totals.overall.open_rate, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                  {totals.overall.open_rate === null && '—'}
                </>
              )}
              {showOpenCount && formatNumber(totals.overall.open_count)}
            </div>
            <span
              className={classNames(
                'inline-block px-1 py-0.5 text-sm font-medium',
                totals.today.open_count > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
              )}
            >
              {t('backend:plus_value_today', { value: formatNumber(totals.today.open_count) })}
            </span>
          </div>
          <div
            className="p-6 space-y-2"
            onMouseEnter={() => setShowClickCount(true)}
            onMouseLeave={() => setShowClickCount(false)}
          >
            <h4 className="font-medium text-slate-500">
              {t('backend:clicked')}
            </h4>
            <div className="text-2xl">
              {!showClickCount && (
                <>
                  {totals.overall.click_rate !== null && formatPercentage(totals.overall.click_rate, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                  {totals.overall.click_rate === null && '—'}
                </>
              )}
              {showClickCount && formatNumber(totals.overall.click_count)}
            </div>
            <span
              className={classNames(
                'inline-block px-1 py-0.5 text-sm font-medium',
                totals.today.click_count > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
              )}
            >
              {t('backend:plus_value_today', { value: formatNumber(totals.today.click_count) })}
            </span>
          </div>
        </Card>

        <div className="grid gap-6 sm:grid-cols-3">
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:recently_sent_mails')}
              </h4>
            </div>
            {recentSentMails.map((sentMail) => (
              <SentMailCard sentMail={sentMail} charity={charity} key={sentMail.id} />
            ))}
            {recentSentMails.length > 0 && recentSentMails.length < activityListMaxLength && (
              <div className="flex flex-1 bg-slate-50/50"></div>
            )}
            {recentSentMails.length === 0 && (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={150} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              </div>
            )}
          </Card>
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:most_clicked_links')}
              </h4>
            </div>
            {topLinks.map((link) => (
              <LinkCard link={link} key={link.id} />
            ))}
            {topLinks.length > 0 && topLinks.length < activityListMaxLength && (
              <div className="flex flex-1 bg-slate-50/50"></div>
            )}
            {topLinks.length === 0 && (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={150} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              </div>
            )}
          </Card>
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:recent_top_custom_mails')}
              </h4>
            </div>
            {topCustomMails.map((mail) => (
              <MailCard mail={mail} key={mail.id} />
            ))}
            {topCustomMails.length > 0 && topCustomMails.length < activityListMaxLength && (
              <div className="flex flex-1 bg-slate-50/50"></div>
            )}
            {topCustomMails.length === 0 && (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={150} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              </div>
            )}
          </Card>
        </div>
      </Container>
    </>
  );
}

MailDashboardPage.layout = (page: ReactNode) => <Layout><DashboardLayout>{page}</DashboardLayout></Layout>;

const SentMailCard = ({
  sentMail,
  charity,
}: {
  sentMail: SentMail & {
    campaign: Campaign & {
      project: Project;
    };
  };
  charity: Charity;
}) => {
  const { formatDate } = useLocale();
  const { routes } = useRouter();

  return (
    <div className="grid grid-cols-[32px_1fr] gap-4 items-center !py-4">
      <div>
        <CampaignLogo campaign={sentMail.campaign} charity={charity} />
      </div>
      <div className="min-w-0 leading-snug">
        <Link href={routes.campaign_sent_mails_page(sentMail.campaign.id)} className="block overflow-hidden font-medium whitespace-nowrap text-ellipsis">
          {sentMail.subject}
        </Link>
        <div className="grid grid-cols-[1fr_fit-content(150px)] gap-4 items-center">
          <div className="overflow-hidden text-sm whitespace-nowrap text-ellipsis text-slate-400">
            {sentMail.campaign.project.title}
          </div>
          <div className="min-w-0 text-right">
            <div className="text-sm text-slate-400">
              {formatDate(sentMail.created_at, 'display_date_time')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LinkCard = ({
  link,
}: {
  link: SentMailLink & {
    click_count: number;
  };
}) => {
  const { formatNumber } = useLocale();

  return (
    <div className="grid grid-cols-[1fr_min-content] gap-2 items-center !py-4 min-h-[75px]">
      <div className="min-w-0 leading-snug">
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block overflow-hidden font-medium whitespace-nowrap text-ellipsis"
          title={link.url}
        >
          {link.url.replace(/^(http(s)?:\/\/)?(www\.)?/i, '')}
        </a>
      </div>
      <div>
        {formatNumber(link.click_count)}×
      </div>
    </div>
  );
};

const MailCard = ({
  mail,
}: {
  mail: Mail & {
    project: Project;
    sent_count: number;
  };
}) => {
  const { formatNumber } = useLocale();
  const { routes } = useRouter();

  return (
    <div className="grid grid-cols-[1fr_min-content] gap-2 !py-4">
      <div className="min-w-0 leading-snug">
        <Link href={routes.project_mails_page(mail.project.id)} className="block overflow-hidden font-medium whitespace-nowrap text-ellipsis">
          <Icon className="mr-1">
            <MailIcon />
          </Icon>
          {mail.title}
        </Link>
        <div className="overflow-hidden text-sm whitespace-nowrap text-ellipsis text-slate-400">
          {mail.project.title}
        </div>
      </div>
      <div>
        {formatNumber(mail.sent_count)}×
      </div>
    </div>
  );
};
